import React, { useState } from 'react';
import { ChevronLeft, BookOpen, Play, Sparkles } from 'lucide-react';
import ChapterPresentations from '../components/ChapterPresentations';

/**
 * SpecializationView - Course list for a specialization
 */
function SpecializationView({ 
  specialization,
  courses,
  questionCounts,
  videoCounts,
  onBack,
  onCourseSelect,
  onStartQuiz
}) {
  const [presCourse, setPresCourse] = useState(null); // {id, name} when presentation panel is open

  if (!specialization) return null;

  const specCourses = specialization.courses || courses || [];

  return (
    <div className="space-y-6 animate-slide-in-right">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button 
          onClick={onBack}
          className="text-slate-400 hover:text-slate-900 transition-colors font-bold flex items-center gap-2"
          data-testid="back-to-specs-button"
        >
          <ChevronLeft className="w-5 h-5" />
          חזרה למסלולים
        </button>
        <button 
          onClick={() => onStartQuiz('governmental', 'hard')}
          className="bg-rose-500 text-white px-4 py-2 rounded-xl font-bold text-xs shadow-md active:scale-95 transition-all"
          data-testid="spec-hard-exam-button"
        >
          מבחן מסכם 🔥
        </button>
      </div>

      {/* Specialization Header */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 rounded-3xl text-white">
        <div className="flex items-center gap-4">
          <span className="text-5xl">{specialization.icon || '📚'}</span>
          <div>
            <h2 className="text-3xl font-black">{specialization.name}</h2>
            <p className="text-white/70">{specCourses.length} קורסים זמינים</p>
          </div>
        </div>
      </div>

      {/* Year Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {['הכל', 'שנה א', 'שנה ב', 'שנה ג', 'שנה ד'].map((year, i) => (
          <button
            key={i}
            className={`px-4 py-2 rounded-full font-bold text-sm whitespace-nowrap transition-all ${
              i === 0 
                ? 'bg-teal-500 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {year}
          </button>
        ))}
      </div>

      {/* Courses Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {specCourses.map((course, index) => {
          const stats = questionCounts[course.id] || {};
          const questionCount = stats.questions || course.question_count || 0;
          const videoCount = videoCounts[course.id] || course.video_count || 0;
          const isComingSoon = course.coming_soon === true;

          return (
            <button
              key={course.id || index}
              onClick={() => {
                if (isComingSoon) return;
                onCourseSelect(course);
              }}
              disabled={isComingSoon}
              aria-disabled={isComingSoon}
              className={`bg-white p-5 rounded-2xl shadow-md border transition-all text-right group ${
                isComingSoon
                  ? 'opacity-60 cursor-not-allowed grayscale-[.4]'
                  : 'hover:border-teal-300 hover:shadow-lg active:scale-95'
              }`}
              data-testid={`course-button-${course.id}`}
            >
              <div className="flex justify-between items-start mb-3">
                <span className={`text-2xl transition-transform ${isComingSoon ? '' : 'group-hover:scale-110'}`}>
                  {course.icon || '📖'}
                </span>
                <div className="flex flex-col items-end gap-1">
                  {isComingSoon && (
                    <span
                      className="bg-amber-100 text-amber-700 text-[10px] font-black px-2 py-1 rounded-full border border-amber-300"
                      data-testid={`coming-soon-badge-${course.id}`}
                    >
                      בקרוב
                    </span>
                  )}
                  {course.year && (
                    <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded-full">
                      שנה {course.year}
                    </span>
                  )}
                </div>
              </div>
              <h3 className="font-black text-slate-900 mb-2 line-clamp-2">{course.name}</h3>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                {isComingSoon ? (
                  <span className="text-amber-700 font-bold">🔒 התוכן יתווסף בקרוב</span>
                ) : (
                  <>
                    {questionCount > 0 && (
                      <span
                        className="flex items-center gap-1"
                        title="סך השאלות במאגר הקורס - בכל תרגול תקבל שאלות אקראיות"
                      >
                        <BookOpen className="w-3 h-3" />
                        {questionCount >= 1000
                          ? `${(questionCount / 1000).toFixed(1)}K+ שאלות`
                          : `${questionCount} שאלות`}
                      </span>
                    )}
                    {videoCount > 0 && (
                      <span className="flex items-center gap-1 text-rose-500">
                        <Play className="w-3 h-3" />
                        {videoCount} סרטונים
                      </span>
                    )}
                    {specialization.id === 'nursing' && (
                      <span
                        role="button"
                        onClick={(e) => { e.stopPropagation(); setPresCourse({ id: course.id, name: course.name }); }}
                        className="flex items-center gap-1 text-teal-600 hover:text-teal-800 cursor-pointer font-bold"
                        data-testid={`open-presentations-${course.id}`}
                      >
                        <Sparkles className="w-3 h-3" />
                        מצגות AI
                      </span>
                    )}
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* No Courses Message */}
      {specCourses.length === 0 && (
        <div className="text-center py-12 bg-white rounded-3xl">
          <div className="text-6xl mb-4">📚</div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">אין קורסים זמינים</h3>
          <p className="text-slate-500">הקורסים יתווספו בקרוב</p>
        </div>
      )}

      {/* AI Presentation viewer */}
      {presCourse && (
        <ChapterPresentations
          courseId={presCourse.id}
          courseName={presCourse.name}
          onClose={() => setPresCourse(null)}
        />
      )}
    </div>
  );
}

export default SpecializationView;
